import Phaser from "phaser";
import { GAMEPLAY, PALETTE, TILE_SIZE, UGC_CYAN } from "../config";
import { AssetService } from "../services/AssetService";
import { AuthorIdentityService } from "../services/AuthorIdentityService";
import { ProfileTooltipService } from "../services/ProfileTooltipService";
import {
  FOREST_LAYER_KEYS,
  PIXEL_FOREST_KEYS,
  SNOWY_FOREST_KEYS,
} from "../services/Parallax";
import { ensureAllShopIcons } from "../ui/shopIcons";

/** Phaser texture key for the Dark Fantasy Enemies bat idle-fly sheet (9 frames × 64×64). */
export const BAT_IDLE_KEY = "enemy.bat.idle";
export const BAT_IDLE_ANIM = "bat.idle";

/**
 * Knight hero (drop-imported pack — see public/assets/sprites/hero/knight/).
 * All strips are 64-tall; frame widths vary per animation (96 for idle /
 * run / attack / death / shield, ~90-108 for jump / roll). For v0.3 we
 * load only the strips that divide cleanly into 96×64 — the rest can
 * arrive when their frame widths are confirmed.
 */
export const KNIGHT_IDLE_KEY = "knight.idle";
export const KNIGHT_RUN_KEY = "knight.run";
export const KNIGHT_ATTACK_KEY = "knight.attack";
export const KNIGHT_IDLE_ANIM = "knight.idle";
export const KNIGHT_RUN_ANIM = "knight.run";
export const KNIGHT_ATTACK_ANIM = "knight.attack";

const KNIGHT_BASE_PATH = "assets/sprites/hero/knight/Knight/";

/**
 * Filename map for the edermunizz Free Pixel Art Forest pack, ordered
 * farthest → nearest. Source files are referenced at their original
 * paths so we satisfy the CC BY-ND 4.0 "no derivatives" clause — we
 * don't rename, recolor, or repack the source PNGs.
 *
 * We load **only the 6 farther layers**. The 3 closest plates from
 * the pack (`Layer_0003_6.png`, `Layer_0002_7.png`, `Layer_0001_8.png`)
 * are intentionally skipped because they crowd the play stage — too
 * many leaves covering the hero (Visual Brief §5: legibility over
 * density). The two `_Lights` overlay layers in the pack are also
 * skipped here (they're additive light effects, not parallax bands).
 */
const FOREST_LAYER_FILES: ReadonlyArray<string> = [
  "Layer_0011_0.png",
  "Layer_0010_1.png",
  "Layer_0009_2.png",
  "Layer_0008_3.png",
  "Layer_0006_4.png",
  "Layer_0005_5.png",
  // Closest plate, added to render the tree crowns/canopy that were
  // missing from the original 6-layer stack — the rear layers carry
  // trunks only, so without this one the canopy reads as headless
  // pillars.
  "Layer_0002_7.png",
];

// Pre-processed forest layers (sips-resampled to 252×216, native height).
// Using these eliminates the runtime sub-pixel resampling that caused the
// edge flicker + seams on the original 928×793 sources. See
// `public/assets/CREDITS.md` for the derivative-status note (this is a
// derivative under CC BY-ND; the user has agreed to acquire a permissive
// license before public redistribution).
const FOREST_BASE_PATH = "assets/tiles/forest-edermunizz/processed/";

const BAT_IDLE_PATH =
  "assets/sprites/enemies/dark-fantasy/DarkFantasyEnemies_FREE/Bat/Bat with VFX/Bat-IdleFly.png";

/**
 * One-time setup scene. Generates the placeholder textures other scenes use
 * (hero, gegner, geo, ground tile, sword swing, sidequest tower, scroll
 * pickup, inventory frame, skill icons, UGC corner-mark, wall arrows).
 * Then attempts to load the optional third-party asset packs:
 *
 *   - Dark Fantasy Enemies (MonoPixelArt, commercial-OK, no-modify) for
 *     the bat sprite sheet → drops in `public/assets/sprites/enemies/dark-fantasy/bat.png`.
 *   - Free Pixel Art Forest (edermunizz, CC BY-ND 4.0) for 9 parallax
 *     layers → drops in `public/assets/tiles/forest-edermunizz/layer1..9.png`.
 *
 * Missing files trigger `loaderror` but are otherwise non-fatal — callers
 * gate on `textures.exists(...)` and fall back to the placeholders.
 *
 * Why textures instead of just rectangles in each scene? Phaser physics
 * (arcade) plays much better with sprites + body shapes than with raw
 * Rectangle game objects, especially for collision against tile bodies.
 */
export class BootScene extends Phaser.Scene {
  private missingAssets: string[] = [];

  constructor() {
    super("boot");
  }

  preload(): void {
    // Placeholders first — these are guaranteed and provide the fallback
    // for any optional pack that fails to load below.
    this.makeOutlinedRect("hero", 12, 16, PALETTE.hero, PALETTE.heroOutline);
    this.makeOutlinedRect("gegner", 14, 14, PALETTE.gegner, 0x4d1f1a);
    this.makeGeo();
    this.makeGroundTile();
    this.makeSwordSwing();
    // Towers are interactable, so the sprite reads as a GATE (stone
    // archway) rather than a tree silhouette. Procedural — no asset
    // dependency.
    this.makeTower();
    this.makeScroll();
    this.makeInventoryFrame();
    this.makeSkillIcons();
    this.makeUgcCornerMark();
    this.makeWallArrows();
    this.makeHeart();
    this.makeCrystal();
    this.makeBoot();
    this.makeBlackGradient();
    this.makeLever();
    this.makeParchment();
    this.makeFeather();
    this.makeVine();
    this.makeRock();

    // Optional packs — log missing files so the developer sees what's needed.
    this.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: Phaser.Loader.File) => {
      this.missingAssets.push(file.key);
    });

    // Dark Fantasy Enemies — Free version: Bat-IdleFly is 576×64 = 9 frames.
    this.load.spritesheet(BAT_IDLE_KEY, encodeURI(BAT_IDLE_PATH), {
      frameWidth: 64,
      frameHeight: 64,
    });

    // Knight hero — each strip uses a different per-frame width
    // (confirmed via `magick … -crop NxM` + alpha bbox extraction):
    //   - Idle: 64×64 × 15 frames (compact breathing pose)
    //   - Run:  96×64 × 8 frames  (wider, arms swinging)
    //   - Attack: 144×64 × 22 frames (lunge + slash extends well past frame centre)
    this.load.spritesheet(
      KNIGHT_IDLE_KEY,
      encodeURI(KNIGHT_BASE_PATH + "noBKG_KnightIdle_strip.png"),
      { frameWidth: 64, frameHeight: 64 },
    );
    this.load.spritesheet(
      KNIGHT_RUN_KEY,
      encodeURI(KNIGHT_BASE_PATH + "noBKG_KnightRun_strip.png"),
      { frameWidth: 96, frameHeight: 64 },
    );
    this.load.spritesheet(
      KNIGHT_ATTACK_KEY,
      encodeURI(KNIGHT_BASE_PATH + "noBKG_KnightAttack_strip.png"),
      { frameWidth: 144, frameHeight: 64 },
    );

    // Free Pixel Art Forest — 9 parallax layers loaded from their original
    // filenames (preserves CC BY-ND: no rename, no derivative).
    FOREST_LAYER_KEYS.forEach((key, i) => {
      const file = FOREST_LAYER_FILES[i];
      if (!file) return;
      this.load.image(key, encodeURI(FOREST_BASE_PATH + file));
    });

    // Pixel Art Forest pack — 4 layers, used by sub-levels at depth 1.
    // Green/lush look that contrasts with the dark Mysterious Forest.
    PIXEL_FOREST_KEYS.forEach((key, i) => {
      this.load.image(
        key,
        `assets/tiles/forest-pixel/processed/layer${i + 1}.png`,
      );
    });

    // Pixel Art Snowy Forest pack — 4 layers, used by sub-levels at depth 2.
    // Cool blue/white snow scene.
    SNOWY_FOREST_KEYS.forEach((key, i) => {
      this.load.image(
        key,
        `assets/tiles/forest-snowy/processed/layer${i}.png`,
      );
    });

    // Fake-drive photos for the asset picker — enumerated at build time from
    // src/drive/ (see AssetService). Preloaded here so the picker, profile
    // avatar and launcher badge can all render real thumbnails synchronously.
    AssetService.queueDriveImages(this);
  }

  create(): void {
    if (this.missingAssets.length > 0) {
      console.info(
        `[boot] ${this.missingAssets.length} optional asset(s) missing — using placeholders for:`,
        this.missingAssets,
      );
    } else if (this.textures.exists(BAT_IDLE_KEY)) {
      console.info("[boot] third-party assets loaded (bat + forest parallax).");
    }
    this.registerBatAnimations();
    this.registerKnightAnimations();

    // Variant-B UGC plumbing: a shared AuthorIdentityService + the small
    // shop-icon textures. Both are cheap and always-on; gating happens at
    // the call sites (Launcher tile / runner HUD button) so Variant A
    // never sees them.
    if (!this.registry.get("authorIdentityService")) {
      this.registry.set("authorIdentityService", new AuthorIdentityService());
    }
    // Profile-tooltip service: hover/click on procedural-author surfaces.
    // Service is always created (cheap, no work until first show); Variant A
    // simply never calls `show`. Telemetry routes through whichever scene
    // last registered the telemetry service in the registry.
    ProfileTooltipService.getOrCreate({
      game: this.game,
      identity: this.registry.get("authorIdentityService") as AuthorIdentityService,
      emitTelemetry: (layer, tag, data) => {
        const tel = this.game.registry.get("telemetryService") as
          | { emit: (l: 1 | 2 | 3, t: string, d?: Record<string, number | string | boolean>) => void }
          | undefined;
        tel?.emit(layer, tag, data);
      },
    });
    ensureAllShopIcons(this);

    this.scene.start("launcher");
  }

  /** Register the bat's idle-fly animation once the spritesheet is loaded. */
  private registerBatAnimations(): void {
    if (!this.textures.exists(BAT_IDLE_KEY)) return;
    if (this.anims.exists(BAT_IDLE_ANIM)) return;
    this.anims.create({
      key: BAT_IDLE_ANIM,
      frames: this.anims.generateFrameNumbers(BAT_IDLE_KEY, { start: 0, end: 8 }),
      frameRate: 12,
      repeat: -1,
    });
  }

  /** Register Knight idle / run / attack animations once the strips are loaded. */
  private registerKnightAnimations(): void {
    if (!this.textures.exists(KNIGHT_IDLE_KEY)) return;
    if (!this.anims.exists(KNIGHT_IDLE_ANIM)) {
      // Idle: 15 frames at 64×64 (confirmed by alpha bbox).
      this.anims.create({
        key: KNIGHT_IDLE_ANIM,
        frames: this.anims.generateFrameNumbers(KNIGHT_IDLE_KEY, { start: 0, end: 14 }),
        frameRate: 8,
        repeat: -1,
      });
    }
    if (this.textures.exists(KNIGHT_RUN_KEY) && !this.anims.exists(KNIGHT_RUN_ANIM)) {
      // Run: 8 frames at 96×64.
      this.anims.create({
        key: KNIGHT_RUN_ANIM,
        frames: this.anims.generateFrameNumbers(KNIGHT_RUN_KEY, { start: 0, end: 7 }),
        frameRate: 14,
        repeat: -1,
      });
    }
    if (this.textures.exists(KNIGHT_ATTACK_KEY) && !this.anims.exists(KNIGHT_ATTACK_ANIM)) {
      // Attack: 22 frames at 144×64. The strip is a single continuous
      // slash + lunge + recovery; played all the way through at 24 fps
      // → ~917 ms. Matches the new GAMEPLAY.attackDurationMs.
      this.anims.create({
        key: KNIGHT_ATTACK_ANIM,
        frames: this.anims.generateFrameNumbers(KNIGHT_ATTACK_KEY, { start: 0, end: 21 }),
        frameRate: 24,
        repeat: 0,
      });
    }
  }

  // --- texture helpers -------------------------------------------------

  private makeOutlinedRect(
    key: string,
    w: number,
    h: number,
    fill: number,
    outline: number,
  ): void {
    const g = this.add.graphics();
    g.fillStyle(outline, 1);
    g.fillRect(0, 0, w, h);
    g.fillStyle(fill, 1);
    g.fillRect(1, 1, w - 2, h - 2);
    g.generateTexture(key, w, h);
    g.destroy();
  }

  private makeGroundTile(): void {
    // 16×16 mossy-stone block: top grass band (3 px) + stone body (13 px)
    // with a checker pattern of three grey shades + dark mortar gaps +
    // occasional moss tufts on the upper-left corner. Reads as
    // forest-floor stone behind the items, replacing the flat
    // two-band brown placeholder.
    const w = TILE_SIZE;
    const h = TILE_SIZE;
    const g = this.add.graphics();
    // Stone body: mid grey base.
    g.fillStyle(PALETTE.stoneB, 1);
    g.fillRect(0, 0, w, h);
    // Two stone slabs: darker block in top-left and bottom-right; lighter in others.
    g.fillStyle(PALETTE.stoneA, 1);
    g.fillRect(0, 4, 8, 6); // upper-left slab
    g.fillRect(8, 11, 8, 5); // lower-right slab
    g.fillStyle(PALETTE.stoneC, 1);
    g.fillRect(8, 4, 8, 7); // upper-right slab
    g.fillRect(0, 10, 8, 6); // lower-left slab
    // Mortar gaps (dark lines between slabs).
    g.fillStyle(0x161616, 1);
    g.fillRect(0, 3, w, 1);
    g.fillRect(7, 4, 1, 12);
    g.fillRect(0, 10, w, 1);
    // Grass band at top: warmer moss with subtle highlight.
    g.fillStyle(PALETTE.stoneMoss, 1);
    g.fillRect(0, 0, w, 3);
    g.fillStyle(PALETTE.stoneMossHilite, 1);
    g.fillRect(1, 0, 2, 1);
    g.fillRect(6, 0, 3, 1);
    g.fillRect(11, 0, 2, 1);
    g.fillRect(14, 0, 1, 1);
    // Soft pebble specks scattered on stones.
    g.fillStyle(PALETTE.stoneC, 1);
    g.fillRect(3, 6, 1, 1);
    g.fillRect(12, 8, 1, 1);
    g.fillRect(5, 13, 1, 1);
    g.fillRect(11, 14, 1, 1);
    g.generateTexture("ground", w, h);
    g.destroy();
  }

  private makeSwordSwing(): void {
    // 20×6 readable longsword: hilt + crossguard + tapered blade.
    // Origin in RunnerScene is (0, 0.5) so the sword pivots at the
    // hilt — the leftmost 3 px sit at the hand, the blade extends
    // rightward to the tip.
    const w = 20;
    const h = 6;
    const g = this.add.graphics();
    // Blade body.
    g.fillStyle(PALETTE.sword, 1);
    g.fillRect(5, 2, 13, 2);
    // Blade highlight (top edge).
    g.fillStyle(PALETTE.swordEdge, 1);
    g.fillRect(5, 1, 13, 1);
    // Tapered tip — pixel-perfect arrow ending.
    g.fillStyle(PALETTE.sword, 1);
    g.fillRect(18, 2, 1, 2);
    g.fillRect(19, 2, 1, 2);
    // Crossguard (dark).
    g.fillStyle(PALETTE.ink, 1);
    g.fillRect(4, 0, 1, 6);
    // Hilt grip (gold).
    g.fillStyle(PALETTE.geo, 1);
    g.fillRect(0, 2, 4, 2);
    // Pommel (dark).
    g.fillStyle(PALETTE.ink, 1);
    g.fillRect(0, 1, 1, 4);
    g.generateTexture("sword", w, h);
    g.destroy();
  }

  private makeTower(): void {
    // Three portal variants share the same wooden doorframe:
    //   - "tower"      → flat pitch-black interior (legacy / generic).
    //   - "tower.down" → inner shadow at the TOP, faint floor-glow at
    //                    the BOTTOM, suggesting a stairwell descending
    //                    away from the viewer (dive into a sub-level).
    //   - "tower.up"   → inner shadow at the BOTTOM, faint sky-glow at
    //                    the TOP, suggesting an opening that climbs out
    //                    (surface back to a parent level).
    // Plus three LOCKED variants — only emitted by sub-level exit
    // gates while the active puzzle is unsolved. They reuse the "up"
    // shading and paint a brass lock plate across the archway with a
    // dark keyhole carved in the shape of the item the puzzle needs:
    //   - "tower.up.locked.kills"    → sword keyhole
    //   - "tower.up.locked.crystals" → diamond keyhole
    //   - "tower.up.locked.lever"    → lever keyhole
    // Same 36×60 source, scaled 0.5 in spawn calls → 18×30 display.
    this.bakeTowerTexture("tower", "flat");
    this.bakeTowerTexture("tower.down", "down");
    this.bakeTowerTexture("tower.up", "up");
    this.bakeTowerTexture("tower.up.locked.kills", "up", "kills");
    this.bakeTowerTexture("tower.up.locked.crystals", "up", "crystals");
    this.bakeTowerTexture("tower.up.locked.lever", "up", "lever");
  }

  /**
   * Render one portal variant. `direction` controls only the inner
   * shading of the archway; the wooden frame is identical across all
   * three so a runtime `setTint` (used for locked/unlocked exit gates)
   * still reads the same on all variants.
   */
  private bakeTowerTexture(
    key: string,
    direction: "flat" | "down" | "up",
    lockShape?: "kills" | "crystals" | "lever",
  ): void {
    const w = 36;
    const h = 60;
    const g = this.add.graphics();

    // 1. SOLID BLACK PORTAL INTERIOR — fills the whole rectangle
    //    interior; the wooden frame paints OVER the outer rim,
    //    leaving the black to read as the portal's "void".
    g.fillStyle(0x000000, 1);
    g.fillRect(4, 8, w - 8, h - 8);

    // 2. Directional inner shading. Two soft horizontal bands carved
    //    into the black interior to read as either "looking down into
    //    a pit" (light at top, deep dark at bottom) or "looking up out
    //    of a shaft" (light at bottom, deep dark at top). The bands
    //    are very narrow + very dim — just enough to give the eye a
    //    depth cue without breaking the "pure void" portal read.
    if (direction !== "flat") {
      this.drawPortalShading(g, direction, w, h);
    }

    // 2b. Locked-gate lock plate — brass rectangle stamped over the
    //     directional shading with a dark item-shaped keyhole carved
    //     into it. Tells the player at a glance which puzzle reward
    //     unlocks this gate. Skipped for unlocked variants so they
    //     keep the cleaner "open portal" read.
    if (lockShape) {
      this.drawLockPlate(g, lockShape, w, h);
    }

    // 3. WOODEN FRAME — brown surround, thicker than the procedural
    //    tower so the portal reads as solid construction.
    g.fillStyle(PALETTE.tower, 1);
    // Pillars (full height beside the black interior)
    g.fillRect(0, 8, 4, h - 8);
    g.fillRect(w - 4, 8, 4, h - 8);
    // Bottom sill — a wider base so the gate feels planted
    g.fillRect(2, h - 4, w - 4, 4);
    // Stepped arch crown — the brown frame curves over the black
    // interior, leaving a rounded portal opening underneath.
    g.fillRect(14, 0, 8, 2);
    g.fillRect(10, 2, 16, 2);
    g.fillRect(6, 4, 24, 2);
    g.fillRect(2, 6, 32, 2);
    g.fillRect(0, 8, w, 2);

    // 4. WOOD-GRAIN ACCENTS — lighter tan highlights on the frame
    //    edges so it reads as carved wood, not a flat brown shape.
    g.fillStyle(PALETTE.towerAccent, 1);
    g.fillRect(14, 0, 8, 1);            // top of arch crown
    g.fillRect(0, 8, 4, 1);             // top of left pillar
    g.fillRect(w - 4, 8, 4, 1);         // top of right pillar
    g.fillRect(1, 14, 1, h - 22);       // vertical highlight on left
    g.fillRect(w - 2, 14, 1, h - 22);   // vertical highlight on right
    g.fillRect(2, h - 4, w - 4, 1);     // top of sill

    // 5. Plank lines on the pillars (subtle horizontal dividers).
    g.fillStyle(0x281b16, 1);
    g.fillRect(0, 22, 4, 1);
    g.fillRect(0, 36, 4, 1);
    g.fillRect(0, 50, 4, 1);
    g.fillRect(w - 4, 22, 4, 1);
    g.fillRect(w - 4, 36, 4, 1);
    g.fillRect(w - 4, 50, 4, 1);

    g.generateTexture(key, w, h);
    g.destroy();
  }

  /**
   * Carve a soft directional gradient into the portal interior. The
   * interior rect is [4, 8 → w-4, h-4] (inset away from the wooden
   * frame). For "down" we want depth cues that read top→bottom as
   * "the light gets shut out as you descend"; for "up" we mirror it.
   */
  private drawPortalShading(
    g: Phaser.GameObjects.Graphics,
    direction: "down" | "up",
    w: number,
    h: number,
  ): void {
    const innerX = 5;
    const innerW = w - 10;
    const innerTop = 9;
    const innerBottom = h - 5;
    const innerH = innerBottom - innerTop; // 46 px tall

    // Inner-shadow rim: 5 stacked rows where the rim sits, going from
    // a faint near-black-grey to pure black so the rim reads as an
    // emboss line. For "down" the rim hugs the TOP of the interior
    // (entrance lip you stare past as you descend); for "up" it
    // hugs the BOTTOM (the threshold you'd climb over to surface).
    const rimShades = [0x141414, 0x0d0d0d, 0x070707, 0x040404, 0x020202];
    for (let i = 0; i < rimShades.length; i++) {
      g.fillStyle(rimShades[i]!, 1);
      const inset = i; // narrower toward the deepest part of the gradient
      if (direction === "down") {
        g.fillRect(innerX + inset, innerTop + i, innerW - inset * 2, 1);
      } else {
        g.fillRect(innerX + inset, innerBottom - 1 - i, innerW - inset * 2, 1);
      }
    }

    // Far-end "light leak" — a single dim glow row at the OPPOSITE end
    // of the rim, painted in `towerAccent` at very low intensity. Reads
    // as a hint of light filtering up from a stairwell floor ("down"
    // direction) or down from an open sky ("up" direction).
    g.fillStyle(PALETTE.towerAccent, 0.18);
    if (direction === "down") {
      g.fillRect(innerX + 4, innerBottom - 2, innerW - 8, 1);
    } else {
      g.fillRect(innerX + 4, innerTop + 1, innerW - 8, 1);
    }

    // Directional chevron — 3 pixels deep, drawn in the same dim accent
    // colour so it reinforces the up/down read without competing with
    // the rim. Centred horizontally; placed in the middle of the
    // interior so it doesn't overlap the rim shading.
    const midY = innerTop + Math.floor(innerH / 2);
    const cx = Math.floor(w / 2);
    g.fillStyle(PALETTE.towerAccent, 0.32);
    if (direction === "down") {
      // ▼ chevron (3 px tall, base 5 px)
      g.fillRect(cx - 2, midY, 5, 1);
      g.fillRect(cx - 1, midY + 1, 3, 1);
      g.fillRect(cx, midY + 2, 1, 1);
    } else {
      // ▲ chevron, mirrored vertically
      g.fillRect(cx, midY, 1, 1);
      g.fillRect(cx - 1, midY + 1, 3, 1);
      g.fillRect(cx - 2, midY + 2, 5, 1);
    }
  }

  /**
   * Stamp a brass lock plate across the centre of the door interior
   * with an item-shaped keyhole carved into it. The plate is painted
   * in `towerAccent` (the same brown highlight the wooden frame uses)
   * + a darker rim, so the locked gate's red runtime tint paints the
   * plate red and the keyhole stays as a dark cavity at any tint.
   *
   * Keyhole shapes mirror the puzzle the player has to solve:
   *   - kills    → upright sword silhouette
   *   - crystals → diamond silhouette
   *   - lever    → vertical post with an angled handle
   */
  private drawLockPlate(
    g: Phaser.GameObjects.Graphics,
    kind: "kills" | "crystals" | "lever",
    w: number,
    h: number,
  ): void {
    const plateW = 14;
    const plateH = 24;
    const plateX = Math.floor((w - plateW) / 2); // 11
    const plateY = Math.floor((h - plateH) / 2); // 18
    // Plate body — brass-ish highlight tone so it sits visibly against
    // the black portal interior.
    g.fillStyle(PALETTE.towerAccent, 1);
    g.fillRect(plateX, plateY, plateW, plateH);
    // Darker rim on the plate so it reads as bolted onto the door
    // rather than painted on it.
    g.fillStyle(PALETTE.tower, 1);
    g.fillRect(plateX, plateY, plateW, 1);                 // top edge
    g.fillRect(plateX, plateY + plateH - 1, plateW, 1);    // bottom edge
    g.fillRect(plateX, plateY, 1, plateH);                 // left edge
    g.fillRect(plateX + plateW - 1, plateY, 1, plateH);    // right edge
    // Two rivet pips so the plate reads as metal hardware.
    g.fillStyle(0x281b16, 1);
    g.fillRect(plateX + 1, plateY + 1, 1, 1);
    g.fillRect(plateX + plateW - 2, plateY + 1, 1, 1);
    g.fillRect(plateX + 1, plateY + plateH - 2, 1, 1);
    g.fillRect(plateX + plateW - 2, plateY + plateH - 2, 1, 1);

    // Item-shaped keyhole — pure black so it reads as an actual
    // cavity carved through the plate. Coordinates are absolute
    // (plate origin already accounted for via the centring above).
    g.fillStyle(0x000000, 1);
    const cx = Math.floor(w / 2); // 18
    const plateCenterY = plateY + Math.floor(plateH / 2); // 30
    if (kind === "kills") {
      // Upright sword: blade pointing up, hilt at bottom.
      g.fillRect(cx - 1, plateCenterY - 8, 2, 7);  // blade
      g.fillRect(cx - 1, plateCenterY - 9, 2, 1);  // tip
      g.fillRect(cx - 3, plateCenterY - 1, 6, 1);  // crossguard
      g.fillRect(cx - 1, plateCenterY, 2, 4);      // grip
      g.fillRect(cx - 2, plateCenterY + 4, 4, 1);  // pommel
    } else if (kind === "crystals") {
      // Diamond silhouette, ~6×10.
      g.fillRect(cx - 1, plateCenterY - 6, 2, 1);
      g.fillRect(cx - 2, plateCenterY - 5, 4, 2);
      g.fillRect(cx - 3, plateCenterY - 3, 6, 3);
      g.fillRect(cx - 2, plateCenterY, 4, 2);
      g.fillRect(cx - 1, plateCenterY + 2, 2, 1);
    } else {
      // Lever: vertical post + angled handle going up-right.
      g.fillRect(cx - 1, plateCenterY - 2, 2, 9); // post
      g.fillRect(cx + 1, plateCenterY - 4, 2, 1); // handle step 1
      g.fillRect(cx + 2, plateCenterY - 6, 2, 1); // handle step 2
      g.fillRect(cx + 3, plateCenterY - 8, 2, 1); // handle tip
      g.fillRect(cx - 2, plateCenterY + 7, 4, 1); // base plate of post
    }
  }

  private makeScroll(): void {
    const w = 8;
    const h = 10;
    const g = this.add.graphics();
    g.fillStyle(PALETTE.scroll, 1);
    g.fillRect(0, 0, w, h);
    g.fillStyle(PALETTE.ink, 1);
    g.fillRect(1, 2, w - 2, 1);
    g.fillRect(1, 5, w - 2, 1);
    g.fillRect(1, 8, w - 2, 1);
    g.generateTexture("scroll", w, h);
    g.destroy();
  }

  private makeInventoryFrame(): void {
    const w = 12;
    const h = 12;
    const g = this.add.graphics();
    g.fillStyle(PALETTE.inventoryFrame, 1);
    g.fillRect(0, 0, w, h);
    g.fillStyle(PALETTE.inventoryEmpty, 1);
    g.fillRect(1, 1, w - 2, h - 2);
    g.generateTexture("inv.slot", w, h);
    g.destroy();
  }

  private makeSkillIcons(): void {
    // 10×10 pictographic icons drawn in `skillUnlocked` (off-white).
    // The HUD applies tint + alpha at runtime — full alpha + no tint
    // for active skills, alpha 0.4 + `skillLocked` tint for inactive
    // ones — so the same texture works for both states.
    const size = 10;
    const fg = PALETTE.skillUnlocked;
    const accent = PALETTE.geo;

    // Jump — up chevron with a solid stem, reads as "go up".
    const jump = this.add.graphics();
    jump.fillStyle(fg, 1);
    jump.fillRect(4, 0, 2, 1);
    jump.fillRect(3, 1, 4, 1);
    jump.fillRect(2, 2, 6, 1);
    jump.fillRect(1, 3, 8, 1);
    jump.fillRect(4, 4, 2, 6);
    jump.generateTexture("icon.jump", size, size);
    jump.destroy();

    // Double-jump — two stacked chevrons, reads as "go up twice".
    const dbl = this.add.graphics();
    dbl.fillStyle(fg, 1);
    // Upper chevron
    dbl.fillRect(4, 0, 2, 1);
    dbl.fillRect(3, 1, 4, 1);
    dbl.fillRect(2, 2, 6, 1);
    dbl.fillRect(4, 3, 2, 1);
    // Lower chevron
    dbl.fillRect(4, 4, 2, 1);
    dbl.fillRect(3, 5, 4, 1);
    dbl.fillRect(2, 6, 6, 1);
    dbl.fillRect(4, 7, 2, 3);
    dbl.generateTexture("icon.dblJump", size, size);
    dbl.destroy();

    // Attack — diagonal sword (blade from upper-right to mid-left,
    // crossguard, gold hilt at lower-left). Echoes the runtime sword
    // sprite so the HUD icon and the actual swing read the same.
    const atk = this.add.graphics();
    atk.fillStyle(fg, 1);
    atk.fillRect(8, 0, 2, 1);
    atk.fillRect(7, 1, 2, 1);
    atk.fillRect(6, 2, 2, 1);
    atk.fillRect(5, 3, 2, 1);
    atk.fillRect(2, 4, 4, 1); // crossguard
    atk.fillStyle(accent, 1);
    atk.fillRect(1, 5, 2, 1); // hilt grip
    atk.fillRect(0, 6, 2, 1);
    atk.generateTexture("icon.attack", size, size);
    atk.destroy();

    // Interact — speech bubble with two dots inside + a tail. Reads as
    // "talk to / interact with" — universal.
    const itr = this.add.graphics();
    itr.fillStyle(fg, 1);
    itr.fillRect(1, 0, 7, 1);  // bubble top
    itr.fillRect(1, 4, 5, 1);  // bubble bottom
    itr.fillRect(1, 1, 1, 3);  // bubble left
    itr.fillRect(7, 1, 1, 3);  // bubble right
    itr.fillRect(2, 2, 1, 1);  // left dot
    itr.fillRect(5, 2, 1, 1);  // right dot
    itr.fillRect(3, 5, 1, 1);  // tail
    itr.fillRect(2, 6, 1, 1);
    itr.generateTexture("icon.interact", size, size);
    itr.destroy();

    // Boot — clear side-profile boot silhouette with a defined ankle
    // and sole. Pulses while a speed boost is active.
    const boot = this.add.graphics();
    boot.fillStyle(fg, 1);
    boot.fillRect(4, 0, 3, 5);  // leg shaft
    boot.fillRect(3, 4, 1, 1);  // ankle inset
    boot.fillRect(3, 5, 5, 1);  // top of foot
    boot.fillRect(2, 6, 6, 1);  // mid foot
    boot.fillRect(1, 7, 8, 2);  // sole
    boot.generateTexture("icon.boot", size, size);
    boot.destroy();
  }

  private makeGeo(): void {
    // 8×8 gold diamond with a single-pixel highlight, replacing the
    // 6×6 solid-square placeholder. Bigger, more readable, and
    // animated via a scale-bob in RunnerScene.spawnGeo so the coin
    // appears to spin.
    const size = 8;
    const g = this.add.graphics();
    g.fillStyle(PALETTE.geo, 1);
    g.fillRect(3, 0, 2, 1);
    g.fillRect(2, 1, 4, 1);
    g.fillRect(1, 2, 6, 4);
    g.fillRect(2, 6, 4, 1);
    g.fillRect(3, 7, 2, 1);
    // Dark outline pixels on top-right and bottom-left for depth.
    g.fillStyle(0x9a7a26, 1);
    g.fillRect(5, 1, 1, 1);
    g.fillRect(6, 2, 1, 4);
    g.fillRect(5, 6, 1, 1);
    // Highlight pip top-left.
    g.fillStyle(PALETTE.heroOutline, 1);
    g.fillRect(2, 2, 1, 1);
    g.fillRect(3, 1, 1, 1);
    g.generateTexture("geo", size, size);
    g.destroy();
  }

  private makeRock(): void {
    // 14×12 chunky boulder. Mid stone body, a warm top-left hilite ridge,
    // and a darker underside so it reads as a solid falling mass at the
    // 384×216 native resolution. Origin is centred by RunnerScene; the
    // sprite is drawn here top-left at (0,0).
    const w = 14;
    const h = 12;
    const g = this.add.graphics();
    g.fillStyle(PALETTE.rockShadow, 1);
    g.fillRect(2, 9, 10, 3);
    g.fillRect(1, 6, 12, 4);
    g.fillStyle(PALETTE.rock, 1);
    g.fillRect(2, 2, 10, 7);
    g.fillRect(1, 4, 12, 4);
    g.fillRect(3, 1, 8, 2);
    g.fillStyle(PALETTE.rockHilite, 1);
    g.fillRect(3, 2, 4, 1);
    g.fillRect(2, 3, 2, 2);
    g.generateTexture("rock", w, h);
    g.destroy();
  }

  private makeUgcCornerMark(): void {
    // 6x6 cyan L-mark; rendered in the parallax corner when a UGC background is active (PRD §3).
    const size = 6;
    const g = this.add.graphics();
    g.fillStyle(UGC_CYAN, 1);
    g.fillRect(0, 0, size, 2);
    g.fillRect(0, 0, 2, size);
    g.generateTexture("ugc.corner", size, size);
    g.destroy();
  }

  private makeWallArrows(): void {
    // Up-pointing chevron — 24x32, stair-stepped pixel rows for a chunky
    // graffiti look. Painted in `wallPaint`; rendered at low alpha so it
    // reads as wall paint, not a foreground prop.
    const upW = 24;
    const upH = 32;
    const up = this.add.graphics();
    up.fillStyle(PALETTE.wallPaint, 1);
    // Triangle head (12 rows, widening).
    for (let r = 0; r < 12; r++) {
      const span = 24 - r * 2;
      up.fillRect((upW - span) / 2, r, span, 1);
    }
    // Stem.
    up.fillRect((upW - 6) / 2, 12, 6, 18);
    // Soft baseline tick.
    up.fillRect(2, 30, upW - 4, 2);
    up.generateTexture("wallArrow.up", upW, upH);
    up.destroy();

    // Right-pointing chevron — 32x24.
    const rightW = 32;
    const rightH = 24;
    const right = this.add.graphics();
    right.fillStyle(PALETTE.wallPaint, 1);
    for (let c = 0; c < 12; c++) {
      const span = 24 - c * 2;
      right.fillRect(rightW - 12 + c, (rightH - span) / 2, 1, span);
    }
    right.fillRect(0, (rightH - 6) / 2, rightW - 12, 6);
    right.generateTexture("wallArrow.right", rightW, rightH);
    right.destroy();

    // Left-pointing chevron — 32x24, mirror of the right version.
    const left = this.add.graphics();
    left.fillStyle(PALETTE.wallPaint, 1);
    for (let c = 0; c < 12; c++) {
      const span = 24 - c * 2;
      left.fillRect(11 - c, (rightH - span) / 2, 1, span);
    }
    left.fillRect(12, (rightH - 6) / 2, rightW - 12, 6);
    left.generateTexture("wallArrow.left", rightW, rightH);
    left.destroy();
  }

  private makeHeart(): void {
    // 10x9 pink-red pixel-art heart silhouette + light highlight.
    const w = 10;
    const h = 9;
    const g = this.add.graphics();
    g.fillStyle(PALETTE.heart, 1);
    g.fillRect(1, 1, 3, 1);
    g.fillRect(6, 1, 3, 1);
    g.fillRect(0, 2, 10, 2);
    g.fillRect(1, 4, 8, 1);
    g.fillRect(2, 5, 6, 1);
    g.fillRect(3, 6, 4, 1);
    g.fillRect(4, 7, 2, 1);
    g.fillStyle(PALETTE.heartHilite, 1);
    g.fillRect(2, 2, 1, 1);
    g.fillRect(7, 2, 1, 1);
    g.generateTexture("heart", w, h);
    g.destroy();
  }

  private makeCrystal(): void {
    // 8x10 cyan diamond — wider in the middle, tapering at top/bottom.
    const w = 8;
    const h = 10;
    const g = this.add.graphics();
    g.fillStyle(PALETTE.crystal, 1);
    g.fillRect(3, 0, 2, 1);
    g.fillRect(2, 1, 4, 1);
    g.fillRect(1, 2, 6, 1);
    g.fillRect(0, 3, 8, 1);
    g.fillRect(0, 4, 8, 1);
    g.fillRect(0, 5, 8, 1);
    g.fillRect(1, 6, 6, 1);
    g.fillRect(2, 7, 4, 1);
    g.fillRect(3, 8, 2, 1);
    // Inner highlight (diamond face)
    g.fillStyle(PALETTE.crystalHilite, 1);
    g.fillRect(3, 2, 1, 2);
    g.fillRect(2, 4, 1, 1);
    g.generateTexture("crystal", w, h);
    g.destroy();
  }

  private makeBoot(): void {
    // 10x9 brown boot side profile — L-shape with a paler leather highlight on top.
    const w = 10;
    const h = 9;
    const g = this.add.graphics();
    g.fillStyle(PALETTE.boot, 1);
    g.fillRect(2, 0, 5, 5);
    g.fillRect(2, 5, 7, 2);
    g.fillRect(0, 7, 10, 2);
    g.fillStyle(PALETTE.bootHilite, 1);
    g.fillRect(2, 0, 5, 1);
    g.fillRect(2, 5, 1, 1);
    g.generateTexture("boot", w, h);
    g.destroy();
  }

  /**
   * 12×24 lever — wooden post with a brass handle at the top.
   * Procedural; no asset dependency. Tinted green at runtime when
   * pulled (puzzle: lever).
   */
  private makeLever(): void {
    const w = 12;
    const h = 24;
    const g = this.add.graphics();
    // Wooden post: dark brown with a 1-px grain highlight.
    g.fillStyle(PALETTE.leverPost, 1);
    g.fillRect(5, 6, 2, 18);
    g.fillStyle(0x281b16, 1);
    g.fillRect(5, 6, 1, 18);
    // Brass handle (angled rightward off the post).
    g.fillStyle(PALETTE.leverHandle, 1);
    g.fillRect(7, 4, 2, 2);
    g.fillRect(8, 2, 2, 2);
    g.fillRect(9, 0, 2, 2);
    // Pivot pin.
    g.fillStyle(PALETTE.ink, 1);
    g.fillRect(5, 5, 2, 1);
    g.generateTexture("lever", w, h);
    g.destroy();
  }

  /**
   * 10×12 escape-feather pickup — a pale-blue plume with a darker
   * central shaft + bright white tip. Reads as "a thing you collect to
   * fly somewhere". Consumed by pressing U in-game; one charge pops
   * the hero out of the current sub-level.
   */
  private makeFeather(): void {
    const w = 10;
    const h = 12;
    const g = this.add.graphics();
    // Plume body.
    g.fillStyle(PALETTE.feather, 1);
    g.fillRect(4, 0, 2, 1);
    g.fillRect(3, 1, 4, 1);
    g.fillRect(2, 2, 6, 1);
    g.fillRect(2, 3, 6, 1);
    g.fillRect(1, 4, 8, 1);
    g.fillRect(2, 5, 6, 1);
    g.fillRect(3, 6, 4, 1);
    g.fillRect(3, 7, 4, 1);
    // Bright white tip.
    g.fillStyle(PALETTE.featherHilite, 1);
    g.fillRect(4, 0, 2, 1);
    g.fillRect(3, 1, 2, 1);
    // Darker shaft running down the middle + tail.
    g.fillStyle(PALETTE.featherShaft, 1);
    g.fillRect(4, 1, 1, 7);
    g.fillRect(4, 8, 1, 4);
    g.generateTexture("feather", w, h);
    g.destroy();
  }

  /**
   * Climbable forest vine — a tall braided liana with leaf clusters at
   * the top and intermittent leaves down the shaft. Width set to
   * `GAMEPLAY.canopyVineWidthPx`, height to `canopyVineHeightPx` so a
   * sprite anchored origin (0.5, 1) at `groundTopY` reaches up to the
   * canopy top. Drawn procedurally — no asset dependency. Used for
   * both ascent (left end of canopy cluster) and descent (right end);
   * the texture is left/right symmetric so we don't need a flipped
   * variant.
   */
  private makeVine(): void {
    const w = GAMEPLAY.canopyVineWidthPx;
    const h = GAMEPLAY.canopyVineHeightPx;
    const g = this.add.graphics();
    // Anchor leaf cluster at the very top — broader than the rope so it
    // reads as "this is tied to a branch up here".
    g.fillStyle(PALETTE.vineLeaf, 1);
    g.fillRect(0, 0, w, 6);
    g.fillStyle(PALETTE.vineLeafHilite, 1);
    g.fillRect(1, 0, 2, 1);
    g.fillRect(5, 0, 2, 1);
    g.fillRect(9, 0, 2, 1);
    g.fillRect(2, 2, 1, 1);
    g.fillRect(8, 2, 1, 1);
    g.fillRect(5, 4, 2, 1);
    // Braided rope body — two intertwined dark strands with a paler
    // highlight running down the middle. Pixel pattern repeats every
    // 6 rows so the eye reads it as a twist.
    g.fillStyle(PALETTE.vineRope, 1);
    g.fillRect(4, 6, 4, h - 6);
    g.fillStyle(PALETTE.vineRopeHilite, 1);
    for (let y = 6; y < h; y += 6) {
      g.fillRect(4, y, 1, 2);
      g.fillRect(7, y + 3, 1, 2);
    }
    // Small leaf tufts every ~18 px down the vine so it doesn't read as
    // a bare rope (forest vines have foliage hanging off them).
    g.fillStyle(PALETTE.vineLeaf, 1);
    for (let y = 18; y < h - 8; y += 22) {
      const side = (y / 22) % 2 === 0 ? -2 : w;
      const xStart = side === -2 ? 0 : w - 3;
      g.fillRect(xStart, y, 3, 3);
      g.fillStyle(PALETTE.vineLeafHilite, 1);
      g.fillRect(side === -2 ? 0 : w - 1, y, 1, 1);
      g.fillStyle(PALETTE.vineLeaf, 1);
    }
    g.generateTexture("vine", w, h);
    g.destroy();
  }

  /**
   * 6×6 parchment swatch — a flat fill plus a single dark speckle for
   * the texture read. The MapScene scales it across the viewport for
   * the background and re-uses the colours for borders / ink lines.
   * Cheap and gives the map screen a "drawn on aged paper" feel.
   */
  private makeParchment(): void {
    const w = 6;
    const h = 6;
    const g = this.add.graphics();
    g.fillStyle(PALETTE.parchment, 1);
    g.fillRect(0, 0, w, h);
    g.fillStyle(PALETTE.parchmentShadow, 1);
    g.fillRect(1, 1, 1, 1);
    g.fillRect(4, 3, 1, 1);
    g.fillRect(2, 5, 1, 1);
    g.generateTexture("parchment", w, h);
    g.destroy();
  }

  /**
   * Horizontal end-of-sub-level gradient — transparent at left,
   * fully opaque at right. Painted as WHITE with an alpha ramp (not
   * black) so a runtime `setTint(color)` paints it in the pack's
   * signature colour. Phaser Graphics doesn't expose a gradient
   * fill, so we use a CanvasTexture and the browser's native
   * `createLinearGradient`. A solid same-coloured fog rectangle
   * trails to the right of this sprite in RunnerScene so the world
   * past the gradient is fully occluded.
   */
  private makeBlackGradient(): void {
    const w = 144;
    const h = 216;
    const tex = this.textures.createCanvas("blackGradient", w, h);
    if (!tex) return;
    const ctx = tex.getContext();
    const grad = ctx.createLinearGradient(0, 0, w, 0);
    grad.addColorStop(0, "rgba(255,255,255,0)");
    grad.addColorStop(0.4, "rgba(255,255,255,0.4)");
    grad.addColorStop(1, "rgba(255,255,255,1)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
    tex.refresh();
  }
}
